// src/components/modules/TableCard.jsx
// Take It to the Table: the module's parting gesture. One conversation
// question engineered for people who've never read philosophy, so the
// thinking this app produces has somewhere to go tonight.
//
// The card also carries the live-discussion demand probe: a single
// anonymous tap per device ("I'd join a live discussion on this"),
// counted per module in Firestore — honest evidence for whether readers
// want a room, before anyone builds one. No email capture, no promises.
import React from 'react';
import { MessagesSquare, Hand, Check } from 'lucide-react';
import { getTableCard } from '../../data/tableCards';
import { useDiscussionDemand } from '../../hooks/useReaderSignals';

const TableCard = ({ module }) => {
  const card = getTableCard(module.order);
  const { raised, count, busy, toggle } = useDiscussionDemand(module.id);

  if (!card) return null;

  return (
    <div className="mt-6 rounded-xl border border-bright-gold/30 bg-gradient-to-br from-amber-500/10 via-transparent to-conversation-teal/10 p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-1">
        <MessagesSquare className="w-5 h-5 text-bright-gold" />
        <span className="text-xs font-semibold tracking-wide uppercase text-bright-gold">
          Take it to the table
        </span>
      </div>
      <h3 className="text-xl font-bold text-white mb-3">{card.handle}</h3>
      <p className="text-slate-300 leading-relaxed mb-4">{card.description}</p>
      <blockquote className="border-l-4 border-bright-gold/60 pl-4 py-1 mb-5">
        <p className="text-lg sm:text-xl text-amber-100 font-medium leading-relaxed">
          {card.question}
        </p>
      </blockquote>
      {card.sides && card.sides.length > 0 && (
        <div className="mb-5">
          <p className="text-xs font-semibold tracking-wide uppercase text-slate-400 mb-2">
            The sides you'll hear
          </p>
          <ul className="space-y-1.5">
            {card.sides.map((side) => (
              <li key={side} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-bright-gold/70 mt-1.5 flex-shrink-0" />
                <span>{side}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="pt-4 border-t border-slate-700/60">
        <p className="text-xs font-semibold tracking-wide uppercase text-slate-400 mb-2">
          Hosting notes
        </p>
        <p className="text-sm text-slate-400 leading-relaxed">{card.guidance}</p>
      </div>

      {/* Demand probe: one anonymous hand per device, changeable */}
      <div className="mt-6 pt-5 border-t border-slate-700/60">
        {!raised ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-slate-300 flex-1">
              Would you join a live discussion on this with other readers?
            </p>
            <button
              type="button"
              onClick={toggle}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-conversation-teal/50 text-conversation-teal hover:bg-conversation-teal/10 transition-colors text-sm font-medium disabled:opacity-50"
            >
              <Hand className="w-4 h-4" />
              I'd join
            </button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-slate-300 flex-1 flex items-start gap-2" aria-live="polite">
              <Check className="w-4 h-4 text-conversation-teal flex-shrink-0 mt-0.5" />
              <span>
                Your hand is up.{' '}
                {count === null
                  ? ''
                  : count <= 1
                    ? 'You’re the first here; we’re gauging interest before building anything.'
                    : `${count.toLocaleString()} readers would join a live discussion on this module, counted one per device.`}{' '}
                No promises yet: if enough hands go up, we’ll build the room.
              </span>
            </p>
            <button
              type="button"
              onClick={toggle}
              disabled={busy}
              className="text-xs text-slate-400 hover:text-slate-300 transition-colors underline underline-offset-2 self-start sm:self-auto disabled:opacity-50"
            >
              Put my hand down
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableCard;
